import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Opt-in BB pack upsell row in the cart drawer. Nothing is ever added
 * automatically — the shopper has to check the box themselves, and the box
 * only reflects whatever is actually in the cart. Loaded globally (not
 * gated behind the markup it controls) so the custom element is always
 * defined before its tag first appears — including when the drawer's cart
 * items section is morphed in dynamically after an add-to-cart, rather than
 * arriving via a real page load. A <script> injected via innerHTML/morph
 * never executes, so defining this element only inside the conditional
 * markup meant the checkbox stayed inert until the next full page refresh.
 */
class FoldshotCartUpsell extends HTMLElement {
  connectedCallback() {
    this.check = this.querySelector('[ref="check"]');
    this.spinner = this.querySelector('[ref="spinner"]');
    this.variantId = this.dataset.variantId;
    this.busy = false;
    this.check.addEventListener('change', () => this.#toggle());

    if (!this.#boundSync) {
      this.#boundSync = () => setTimeout(() => this.#sync(), 60);
      document.addEventListener(CartLinesUpdateEvent.eventName, this.#boundSync);
    }
    this.#sync();
  }

  disconnectedCallback() {
    if (this.#boundSync) {
      document.removeEventListener(CartLinesUpdateEvent.eventName, this.#boundSync);
    }
  }

  #boundSync = null;

  // The box always mirrors whatever is actually in the cart right now.
  #sync() {
    if (this.busy) return;
    this.check.checked = this.dataset.inCart === 'true';
  }

  #toggle() {
    if (this.check.checked) {
      this.#add();
    } else {
      this.#remove();
    }
  }

  async #add() {
    await this.#request({ id: this.variantId, quantity: 1 }, 'add');
  }

  async #remove() {
    await this.#request({ id: this.variantId, quantity: 0 }, 'change');
  }

  async #request(body, kind) {
    if (this.busy) return;
    this.busy = true;
    this.spinner.hidden = false;
    this.check.disabled = true;

    // Both endpoints need the `.js` suffix to force a JSON response — without
    // it, `/cart/change` honors our `Accept: text/html` override below and
    // 302-redirects to the full cart page instead, which breaks `.json()`
    // and silently drops every uncheck (the change still happens server
    // side, but the client never finds out, so the box snaps back checked).
    const url = kind === 'add' ? Theme.routes.cart_add_url : `${Theme.routes.cart_change_url}.js`;
    const formData = new FormData();
    Object.entries(body).forEach(([k, v]) => formData.set(k, v));
    formData.append('sections', 'cart-drawer-section');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: kind === 'add' ? 'add' : 'update',
        context: 'cart',
        lines: [{ merchandiseId: this.variantId, quantity: body.quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const cfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(url, { ...cfg, headers: { ...cfg.headers, Accept: 'text/html' } });
      const parsed = await response.json();

      if (parsed.status) {
        throw new Error(parsed.message || parsed.description || 'Cart update failed');
      }

      const cartItems = document.querySelector('cart-items-component');
      let ajaxCart;
      if (cartItems && typeof cartItems.fetchCartData === 'function') {
        await customElements.whenDefined('cart-items-component');
        ajaxCart = await cartItems.fetchCartData();
      } else {
        ajaxCart = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' } }).then((r) =>
          r.json()
        );
      }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'foldshot-cart-upsell',
          didError: false,
          // The parsed response's own `sections` (from the `sections` param
          // above) is what cart-items-component uses to morph the drawer
          // immediately — without this the total only updates on refresh.
          sections: parsed.sections,
        },
      });
    } catch (error) {
      deferred.reject(error);
    } finally {
      this.busy = false;
      this.spinner.hidden = true;
      this.check.disabled = false;
      requestAnimationFrame(() => this.#sync());
    }
  }
}

if (!customElements.get('foldshot-cart-upsell')) {
  customElements.define('foldshot-cart-upsell', FoldshotCartUpsell);
}
